import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const HydrostaticReportDetail: React.FC<{
  heatNumber: string;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, projectLimitFields = [], frequency, measurements = [], deletedMeasurementIds = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const [activeHydroId, setActiveHydroId] = useState<string | null>(null);
  const hydroMeasurements = useMemo(
    () => measurements.filter((measurement) => measurement.reportType === 'HYDROSTATIC'),
    [measurements]
  );
  const hydroMeasurement = hydroMeasurements.find((measurement) => measurement.id === activeHydroId) || hydroMeasurements[0];
  useEffect(() => {
    if (!hydroMeasurements.length) {
      if (activeHydroId) setActiveHydroId(null);
      return;
    }
    if (!activeHydroId || !hydroMeasurements.some((measurement) => measurement.id === activeHydroId)) {
      setActiveHydroId(hydroMeasurements[0].id);
    }
  }, [activeHydroId, hydroMeasurements]);
  const testPressureBar = hydroMeasurement?.hydroTestPressureBar ? Number(hydroMeasurement.hydroTestPressureBar) : 0;
  const holdDurationS = hydroMeasurement?.hydroHoldDurationS ? Number(hydroMeasurement.hydroHoldDurationS) : 0;
  const pressureDropObserved = (hydroMeasurement?.hydroPressureDropObserved || 'no') === 'yes';
  const leakObserved = (hydroMeasurement?.hydroLeakObserved || 'no') === 'yes';
  const minPressureBar = getProjectLimitMin(projectLimitFields, 'test_pressure_bar', 257);
  const minHoldDurationS = getProjectLimitMin(projectLimitFields, 'hold_duration_s', 30);
  const pressureLimitText = getProjectLimitText(projectLimitFields, 'test_pressure_bar', `>= ${minPressureBar} bar`);
  const holdLimitText = getProjectLimitText(projectLimitFields, 'hold_duration_s', `>= ${minHoldDurationS} s`);
  const hasRequiredHydroValues = Boolean(hydroMeasurement?.hydroTestPressureBar && hydroMeasurement?.hydroHoldDurationS);
  const status = hasRequiredHydroValues
    ? testPressureBar >= minPressureBar && holdDurationS >= minHoldDurationS && !pressureDropObserved && !leakObserved ? 'PASS' : 'FAIL'
    : 'INCOMPLETE';
  const curve = hasRequiredHydroValues
    ? [0, 40, 90, 140, 180, 215, 245, 265, testPressureBar, testPressureBar, testPressureBar, testPressureBar, testPressureBar, testPressureBar, testPressureBar, Math.max(0, testPressureBar - 1), Math.max(0, testPressureBar - 10), Math.max(0, testPressureBar - 70), 80, 0]
    : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  const maxPressure = Math.max(...curve, minPressureBar) * 1.08;
  const points = curve.map((value, index) => ({
    x: (index / (curve.length - 1)) * 800,
    y: 150 - (value / maxPressure) * 150,
  }));
  const pressurePath = points.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ');
  const targetY = 150 - (minPressureBar / maxPressure) * 150;
  const passConditions = [
    { label: 'Test pressure', value: hasRequiredHydroValues ? testPressureBar : '-', unit: 'bar', limit: pressureLimitText, ok: testPressureBar >= minPressureBar, threshold: minPressureBar, scaleMax: Math.max(minPressureBar * 1.5, testPressureBar * 1.15) },
    { label: 'Hold duration', value: hasRequiredHydroValues ? holdDurationS : '-', unit: 's', limit: holdLimitText, ok: holdDurationS >= minHoldDurationS, threshold: minHoldDurationS, scaleMax: Math.max(minHoldDurationS * 1.5, holdDurationS * 1.15) },
    { label: 'Pressure drop observed', value: hydroMeasurement ? pressureDropObserved ? 'Yes' : 'No' : '-', unit: '', limit: 'must be false', ok: !pressureDropObserved },
    { label: 'Leak observed', value: hydroMeasurement ? leakObserved ? 'Yes' : 'No' : '-', unit: '', limit: 'must be false', ok: !leakObserved },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Pressure trace</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">
              {hasRequiredHydroValues ? `${testPressureBar} bar held for ${holdDurationS} s - ${pressureDropObserved ? 'drop observed' : 'no drop'}, ${leakObserved ? 'leak observed' : 'no leak'}` : 'No hydrostatic test saved yet'}
            </div>
          </div>
          <Button type="button" size="sm" onClick={() => onAddMeasurement?.('HYDROSTATIC')} className="bg-hero-red text-white hover:bg-hero-red-60">
            <Plus className="mr-2 h-4 w-4" />
            Add Test
          </Button>
        </div>

        <Card className="border-[#E2E5EA]">
          <CardContent className="p-5">
            <svg viewBox="0 0 800 185" className="h-[190px] w-full">
              {[0, 0.25, 0.5, 0.75, 1].map((point) => (
                <line key={point} x1="0" y1={150 * point} x2="800" y2={150 * point} stroke="#EEF0F3" strokeWidth="1" />
              ))}
              <rect x="160" y="0" width="440" height="150" fill="#EAF8EF" opacity="0.45" />
              <text x="380" y="16" fontSize="10" fontFamily="monospace" fill="#1F9A3A" textAnchor="middle">hold {holdDurationS} s</text>
              <line x1="0" y1={targetY} x2="800" y2={targetY} stroke="#B38600" strokeWidth="1" strokeDasharray="4 3" />
              <text x="792" y={targetY - 5} fontSize="10" fontFamily="monospace" fill="#8A6500" textAnchor="end">P_min = {minPressureBar} bar</text>
              <path d={pressurePath} fill="none" stroke="#00273F" strokeWidth="2" />
              <circle cx={points[8]?.x || 0} cy={points[8]?.y || 0} r="3.5" fill="#00273F" />
              <text x={(points[8]?.x || 0) + 8} y={(points[8]?.y || 0) - 5} fontSize="10" fontFamily="monospace" fill="#00273F">{hasRequiredHydroValues ? `${testPressureBar} bar` : 'No data'}</text>
              <text x="0" y="170" fontSize="10" fontFamily="monospace" fill="#969696">0 s</text>
              <text x="800" y="170" fontSize="10" fontFamily="monospace" fill="#969696" textAnchor="end">~ {curve.length * 3} s</text>
            </svg>
          </CardContent>
        </Card>

        {/* <Card className="border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Required minimum pressure - formula" subtitle="P_min = (2 * S * t) / D * 10" />
            <div className="grid grid-cols-2 gap-4 p-4 md:grid-cols-4 xl:grid-cols-7">
              {[
                ['SMYS * 0.90', '403.2 MPa'],
                ['SMTS * 0.72', '381.6 MPa'],
                ['S (min of above)', '381.6 MPa'],
                ['t (WT)', '22.2 mm'],
                ['D (OD)', '660.4 mm'],
                ['P_min calculated', '257 bar'],
                ['P measured', hasRequiredHydroValues ? `${testPressureBar} bar` : '-'],
              ].map(([label, value], index) => (
                <div key={label}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{label}</div>
                  <div className={cn('mt-1 font-heading text-[18px] font-semibold', index >= 5 ? 'text-galvanizing-green' : index === 2 ? 'text-blueprint-blue' : 'text-text-grey')}>
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card> */}

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Pass conditions" />
            {passConditions.map((condition) => (
              <div key={condition.label} className="grid grid-cols-[220px_minmax(0,1fr)_120px] items-center gap-4 border-b border-[#EEF0F3] px-4 py-3 last:border-b-0">
                <div>
                  <div className="text-[12px] font-semibold text-blueprint-blue">{condition.label}</div>
                  <div className="font-mono text-[10px] text-heritage-grey">{condition.limit}</div>
                </div>
                <div className="min-w-0">
                  {typeof condition.value === 'number' && condition.threshold && condition.scaleMax ? (
                    <>
                      <div className="mb-1 flex items-center justify-between font-mono text-[11px] text-text-grey">
                        <span>{condition.value} {condition.unit}</span>
                        <span>limit {condition.threshold} {condition.unit}</span>
                      </div>
                      <div className="relative h-2.5 overflow-hidden rounded-full bg-[#EEF0F3]">
                        <div
                          className={cn('absolute left-0 top-0 h-full rounded-full', condition.ok ? 'bg-[rgba(31,154,58,0.16)]' : 'bg-[rgba(196,0,31,0.14)]')}
                          style={{ width: `${Math.min(100, Math.max(4, (condition.value / condition.scaleMax) * 100))}%` }}
                        />
                        <div
                          className={cn('absolute top-[-4px] h-5 w-[2px] rounded-full', condition.ok ? 'bg-galvanizing-green' : 'bg-readable-red')}
                          style={{ left: `${Math.min(100, Math.max(0, (condition.threshold / condition.scaleMax) * 100))}%` }}
                        />
                      </div>
                      <div className="mt-1 flex justify-between font-mono text-[10px] text-heritage-grey">
                        <span>0</span>
                        <span>{condition.scaleMax.toFixed(condition.unit === 'bar' ? 1 : 0)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="font-mono text-[13px] text-text-grey">
                      {condition.value} {condition.unit}
                    </div>
                  )}
                </div>
                <div className="text-right"><StatusBadge status={hasRequiredHydroValues ? condition.ok ? 'PASS' : 'FAIL' : 'INCOMPLETE'} /></div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Test setup" subtitle={hydroMeasurement ? `Selected test ${hydroMeasurements.findIndex((item) => item.id === hydroMeasurement.id) + 1}` : 'No test selected'} />
            <div className="grid grid-cols-2 gap-4 p-4 md:grid-cols-4">
              {[
                ['Frequency scope', hydroMeasurement?.frequency || frequency || 'per_pipe'],
                ['Medium', hydroMeasurement?.hydroMedium || '-'],
                ['Sealing', hydroMeasurement?.hydroSealingMethod || '-'],
                ['Gauge cal.', hydroMeasurement?.hydroGaugeCalRef || '-'],
                ['Design factor', hydroMeasurement?.hydroDesignFactor || '-'],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{label}</div>
                  <div className="mt-1 font-mono text-[12px] font-semibold text-blueprint-blue">{value}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead
              title="All Hydrostatic Tests"
              subtitle={`${hydroMeasurements.length} user-added items`}
              right={(
                <Button type="button" size="sm" onClick={() => onAddMeasurement?.('HYDROSTATIC')} className="bg-hero-red text-white hover:bg-hero-red-60">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Item
                </Button>
              )}
            />
            {hydroMeasurements.length === 0 ? (
              <div className="p-4 text-[13px] text-heritage-grey">No hydrostatic tests saved yet.</div>
            ) : (
              <table className="w-full text-left">
                <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                  <tr>
                    <th className="px-4 py-3">Frequency</th>
                    <th className="px-4 py-3 text-right">Test pressure</th>
                    <th className="px-4 py-3 text-right">Hold duration</th>
                    <th className="px-4 py-3">Pressure drop</th>
                    <th className="px-4 py-3">Leak observed</th>
                    <th className="px-4 py-3">Medium</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                  {hydroMeasurements.map((measurement) => (
                    <tr key={measurement.id} onClick={() => setActiveHydroId(measurement.id)} className={cn('cursor-pointer', hydroMeasurement?.id === measurement.id && 'bg-[#E7F3FF]')}>
                      <td className="px-4 py-3 font-mono font-semibold">{measurement.frequency || frequency || 'per_pipe'}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.hydroTestPressureBar || '-'}</td>
                      <td className="px-4 py-3 text-right font-mono">{measurement.hydroHoldDurationS || '-'}</td>
                      <td className="px-4 py-3">{measurement.hydroPressureDropObserved === 'yes' ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3">{measurement.hydroLeakObserved === 'yes' ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3">{measurement.hydroMedium || '-'}</td>
                      <td className="px-4 py-3"><StatusBadge status={measurement.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <ItemActionButtons measurement={measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', HYDROSTATIC_DETAIL.reportId, true],
          ['Performed at', formatDisplayDate(HYDROSTATIC_DETAIL.performedAt), true],
          ['Heat', heatNumber, true],
          ['Station', HYDROSTATIC_DETAIL.station],
          ['Operator', HYDROSTATIC_DETAIL.operator],
          ['Specification', HYDROSTATIC_DETAIL.spec],
          ['Source file', HYDROSTATIC_DETAIL.sourceFile, true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={getProjectLimitRows(projectLimitFields, [
          ['Medium', HYDROSTATIC_DETAIL.testMedium],
          ['Pressure', pressureLimitText, true],
          ['Hold', holdLimitText, true],
          ['Pressure drop', `<= ${HYDROSTATIC_DETAIL.requirements.maxPressureDropMpa} MPa`, true],
          ['Leakage', HYDROSTATIC_DETAIL.requirements.leakageAllowed ? 'allowed' : 'not permitted'],
          ['Frequency', frequency || 'per_pipe', true],
        ])}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows
              rows={[
                ['Frequency', hydroMeasurement?.frequency || frequency || 'per_pipe', true],
                ['Heat number', heatNumber || '-', true],
                ['Tests', `${hydroMeasurements.length} saved`, true],
                ['Selected status', <StatusBadge key="status" status={status} />],
              ]}
            />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};

const hydroLimitText = (check: typeof HYDROSTATIC_DETAIL.checks[number]) => {
  if (check.min != null) return `>= ${check.min} ${check.unit}`;
  if (check.max != null) return `<= ${check.max} ${check.unit}`;
  return '-';
};

const hydroScaleMax = (check: typeof HYDROSTATIC_DETAIL.checks[number]) => {
  if (check.key === 'TEST_PRESSURE') return 22;
  if (check.key === 'HOLD_TIME') return 45;
  if (check.key === 'PRESSURE_DROP') return 0.5;
  return 2;
};
